import { INFO_NOT_AVAILABLE_NA, worldLayer } from '@backoffice-frontend/common'
import { useAppearance } from '@backoffice-frontend/dark-mode'
import { ServiceBreakLocationType } from '@backoffice-frontend/graphql'
import {
  computeBoundsFromGeoJson,
  MapPolygon,
  useHereMap,
} from '@backoffice-frontend/here-maps'
import { useTheme } from '@backoffice-frontend/patterns'
import { featureCollection, multiPolygon } from '@turf/helpers'
import union from '@turf/union'
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson'
import { useEffect, useMemo } from 'react'
import { operatingAreaBoundaryStyle } from '../HereMap/const'
import { PureBreakMarker, PureHubMarker } from '../PureMapMarkers'
import { MapMarker } from './MapMarker'

export type ServiceAreaBreakLocations = {
  id: string
  name: string
  type: ServiceBreakLocationType
  polygon: string | null
  vehiclesCount: number
  routableLocation: {
    latitude: number
    longitude: number
  } | null
}
export type OperatingAreasPolygonProps = {
  areas: {
    id: string
    name: string
    geometry: string // MultiPolygon GeoJSON string
  }[]
  serviceBreakLocations: ServiceAreaBreakLocations[]
  showReturningVehiclesLabel?: boolean
  showBreakpoints?: boolean
  onLocationClick?: (locationId: string) => void
  selectedBreakLocationId?: string
  highlightedAreaId?: string
  setMapLoaded?: (loaded: boolean) => void
}

// Convert worldLayer {lat, lng}[] to Location[] for MapPolygon
const worldLayerLocations = worldLayer.map(p => ({
  latitude: p.lat,
  longitude: p.lng,
}))

// Extract outer rings from a GeoJSON geometry as Location[][]
const extractOuterRings = (
  geometry: MultiPolygon | Polygon,
): { latitude: number; longitude: number }[][] => {
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.map(poly =>
      poly[0].map(([lng, lat]: Position) => ({
        latitude: lat,
        longitude: lng,
      })),
    )
  }
  return [
    geometry.coordinates[0].map(([lng, lat]: Position) => ({
      latitude: lat,
      longitude: lng,
    })),
  ]
}

/**
 * OperatingAreasPolygon renders unified polygon geometry on the map for multiple operating areas.
 * Uses @turf/union to merge all overlapping areas into a single unified polygon.
 * Renders serviceBreakLocations as MapMarkers on the map.
 * @param {areas} areas - The areas to display
 * @param {serviceBreakLocations} serviceBreakLocations - The service break locations to display
 * @param {showReturningVehiclesLabel} showReturningVehiclesLabel - Whether the additional returning vehicles label is visible
 * @param {onLocationClick} onLocationClick - Callback when a service break location is clicked
 * @param {selectedBreakLocationId} selectedBreakLocationId - The ID of the currently selected break location
 * @param {setMapLoaded} setMapLoaded - Callback to set the map loaded state
 */
export const OperatingAreasPolygon = ({
  areas,
  serviceBreakLocations,
  showReturningVehiclesLabel,
  showBreakpoints = true,
  onLocationClick,
  selectedBreakLocationId,
  highlightedAreaId,
  setMapLoaded,
}: OperatingAreasPolygonProps) => {
  const { setViewport } = useHereMap()
  const theme = useTheme()
  const { darkMode } = useAppearance()

  const areasToRender = useMemo(() => {
    if (highlightedAreaId) {
      return areas.filter(a => a.id === highlightedAreaId)
    }
    return areas
  }, [areas, highlightedAreaId])

  // Compute the unified geometry from turf union
  const unifiedGeometry = useMemo(() => {
    if (!areasToRender.length) return undefined

    const features = areasToRender
      .map(area => {
        if (!area.geometry) return null

        try {
          const geom = JSON.parse(area.geometry)

          if (geom.type === 'MultiPolygon') {
            return multiPolygon(geom.coordinates)
          }
          return null
        } catch (error) {
          console.error('Failed to parse geometry for area:', area.id, error)
          return null
        }
      })
      .filter((feature): feature is Feature<MultiPolygon> => feature !== null)

    if (features.length === 0) return undefined

    // NOTE: Some regions return only one operating area which results into one feature, if so we use it directly. Otherwise, we need to union all features.
    let finalFeature: Feature<MultiPolygon | Polygon> | null = null
    if (features.length === 1) {
      finalFeature = features[0]
    } else {
      const collection = featureCollection(features)
      finalFeature = union(collection)
    }

    if (!finalFeature) return undefined

    return finalFeature.geometry
  }, [areasToRender])

  // Extract outer rings as Location[][] for rendering
  const outerRings = useMemo(() => {
    if (!unifiedGeometry) return []
    return extractOuterRings(unifiedGeometry)
  }, [unifiedGeometry])

  // Zoom to bounds on initial load
  useEffect(() => {
    if (!areas.length) return

    const geometryStrings = areas
      .map(a => a.geometry)
      .filter((g): g is string => Boolean(g))
    const bounds = computeBoundsFromGeoJson(geometryStrings)
    if (bounds) {
      setViewport(bounds, false)
    }

    setMapLoaded?.(true)
  }, [areas, setViewport, setMapLoaded])

  const boundaryStyle = operatingAreaBoundaryStyle(darkMode)

  const hasCoordinates = (
    location: ServiceAreaBreakLocations,
  ): location is ServiceAreaBreakLocations & {
    routableLocation: { latitude: number; longitude: number }
  } =>
    !!location.routableLocation?.latitude &&
    !!location.routableLocation?.longitude

  return (
    <>
      {/* World overlay with area cutout */}
      {outerRings.length > 0 && (
        <MapPolygon
          points={worldLayerLocations}
          innerRings={outerRings}
          fillColor={boundaryStyle.fillColor}
          strokeColor={boundaryStyle.strokeColor}
          lineWidth={boundaryStyle.lineWidth}
        />
      )}
      {/* Outline for each outer ring */}
      {outerRings.map(ring => (
        <MapPolygon
          key={`${ring[0]?.latitude}-${ring[0]?.longitude}`}
          points={ring}
          fillColor="transparent"
          strokeColor={theme.semantic.ColorContentReduced}
          lineWidth={1}
        />
      ))}
      {/* Hub location polygons */}
      {serviceBreakLocations
        .filter(loc => loc.type === ServiceBreakLocationType.Hub && loc.polygon)
        .map(loc => (
          <MapPolygon
            key={loc.id}
            geoJson={loc.polygon!}
            fillColor={boundaryStyle.fillColor}
            strokeColor={boundaryStyle.strokeColor}
            lineWidth={boundaryStyle.lineWidth}
          />
        ))}
      {/* Break location polygons */}
      {showBreakpoints &&
        serviceBreakLocations
          .filter(
            loc => loc.type === ServiceBreakLocationType.Break && loc.polygon,
          )
          .filter(
            loc =>
              !showReturningVehiclesLabel || loc.id === selectedBreakLocationId,
          )
          .map(loc => (
            <MapPolygon
              key={loc.id}
              geoJson={loc.polygon!}
              fillColor={boundaryStyle.fillColor}
              strokeColor={boundaryStyle.strokeColor}
              lineWidth={boundaryStyle.lineWidth}
            />
          ))}
      {/* Service break location markers */}
      {serviceBreakLocations
        .filter(hasCoordinates)
        .filter(
          location =>
            showBreakpoints || location.type === ServiceBreakLocationType.Hub,
        )
        .filter(
          location =>
            !showReturningVehiclesLabel ||
            location.type === ServiceBreakLocationType.Hub ||
            location.id === selectedBreakLocationId,
        )
        .map(location => {
          const vehiclesCount = location.vehiclesCount
          const isSelected = selectedBreakLocationId === location.id
          const isClickable = !!onLocationClick

          return (
            <MapMarker
              key={location.id}
              id={location.id}
              lat={location.routableLocation?.latitude}
              lng={location?.routableLocation?.longitude}
              handleMarkerClick={() =>
                isClickable ? onLocationClick(location.id) : undefined
              }
            >
              {location.type === ServiceBreakLocationType.Break ? (
                <div
                  css={{
                    position: 'relative',
                    top: isSelected ? -17 : -13,
                  }}
                >
                  <PureBreakMarker
                    locationName={location.name ?? INFO_NOT_AVAILABLE_NA}
                    showReturningVehiclesLabel={showReturningVehiclesLabel}
                    vehicleCount={vehiclesCount}
                    isClickable={isClickable}
                    isSelected={isSelected}
                  />
                </div>
              ) : (
                <PureHubMarker
                  hubName={location.name ?? INFO_NOT_AVAILABLE_NA}
                  showReturningVehiclesLabel={showReturningVehiclesLabel}
                  vehicleCount={vehiclesCount}
                  isClickable={isClickable}
                  isSelected={isSelected}
                />
              )}
            </MapMarker>
          )
        })}
    </>
  )
}
